import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout, { PageHeader } from "@/components/Layout";
import { DiscBadge, DISC } from "@/components/badges";
import HazardBanner from "@/components/HazardBanner";
import TelephoneIntel from "@/components/TelephoneIntel";
import ScenarioDebrief from "@/components/ScenarioDebrief";
import { FallbackRow, EscalationBanner, DuplicateWarning } from "@/components/CallIntakeAids";
import { AddressInput } from "@/components/AddressInput";
import { IntakeLocate } from "@/components/IntakeLocate";
import { AniInput } from "@/components/AniInput";
import { NearbyOpenCalls } from "@/components/NearbyOpenCalls";
import { LateEntryPanel } from "@/components/LateEntryPanel";
import { toast } from "sonner";
import {
  Search, PhoneCall, Radio, HeartPulse, ClipboardList, Loader2, Sparkles, Map,
  User, ShieldAlert, CheckSquare, Square, Siren, AlertTriangle, Hash, Phone, Zap,
} from "lucide-react";

const PRIORITIES = [[0, "P0 — Responder Emergency"], [1, "P1 — Critical"], [2, "P2 — High"], [3, "P3 — Urgent"], [4, "P4 — Routine"], [5, "P5 — Low"]];
const CRIT = /\b(cpr|aed|not breathing|do not stop|hands-?only|do not go back|get everyone out|tourniquet|stop the bleeding|compress)/i;

export default function NewCall() {
  const [disc, setDisc] = useState("police");
  const { user } = useAuth();
  const [origin, setOrigin] = useState(() => new URLSearchParams(window.location.search).get("origin") === "officer" ? "officer" : "citizen");
  const [lateEntry, setLateEntry] = useState(null);
  const lateEntryPayload = () => lateEntry ? {
    received_at: new Date(lateEntry.received_at).toISOString(), reason: lateEntry.reason, recording_ref: lateEntry.recording_ref || null,
    notes: lateEntry.notes.filter((n) => n.text.trim()).map((n) => ({ ts: new Date(n.ts).toISOString(), text: n.text, unit: n.unit || null })),
    units: lateEntry.units.split(",").map((u) => u.trim()).filter(Boolean),
    closed_at: lateEntry.closed_at ? new Date(lateEntry.closed_at).toISOString() : null,
    disposition_code: lateEntry.disposition_code || null, disposition_narrative: lateEntry.disposition_narrative || null,
  } : undefined;
  const [q, setQ] = useState("");
  const [types, setTypes] = useState([]);
  const [detail, setDetail] = useState(null);
  const [priority, setPriority] = useState(3);
  const [answers, setAnswers] = useState({});
  const [given, setGiven] = useState({});
  const [addr, setAddr] = useState(""); const [apt, setApt] = useState("");
  const [geo, setGeo] = useState(null);
  const [locateToken, setLocateToken] = useState(null);
  const [beat, setBeat] = useState(""); const [district, setDistrict] = useState(""); const [jur, setJur] = useState("Metro");
  const [commonName, setCommonName] = useState(""); const [ctx, setCtx] = useState(null);
  const [routed, setRouted] = useState(null);
  const routeAgency = async (mode, override = null) => {
    const agency = override?.agency || ctx.jurisdiction;
    const psap = override?.psap || ctx.psap || {};
    try {
      const r = await client.post("/ali/agency-route", { mode, agency, psap, address: addr, common_name: commonName || null, caller: { name: callerName || null, phone: callerPhone || null }, call_type: detail?.label || null, cc_number: cc || null });
      setRouted(mode);
      toast.success(mode === "transfer" ? `Transfer logged → ${agency}` : `Mutual-aid notice sent → ${agency}`);
      if (mode === "transfer" && r.data.tel) window.open(`tel:${r.data.tel}`, "_self");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not route"); }
  };
  const autoRef = useRef({ beat: "", district: "", jur: "Metro" });
  useEffect(() => {
    if (!geo?.lat || !geo?.lng) return;
    client.get("/ali/address-context", { params: { lat: geo.lat, lng: geo.lng, address: addr } }).then((r) => {
      const c = r.data; setCtx(c);
      const a = autoRef.current;
      if (c.beat && (!beat || beat === a.beat)) { setBeat(c.beat); a.beat = c.beat; }
      if (c.district && (!district || district === a.district)) { setDistrict(c.district); a.district = c.district; }
      if (c.jurisdiction && (!jur || jur === a.jur)) { setJur(c.jurisdiction); a.jur = c.jurisdiction; }
      if (c.common_name) setCommonName(c.common_name);
    }).catch(() => setCtx(null));
  }, [geo?.lat, geo?.lng]);
  const [callerName, setCallerName] = useState(""); const [callerPhone, setCallerPhone] = useState(""); const [ali, setAli] = useState("");
  const [ofc, setOfc] = useState({ reporting_unit: "", officer_name: "", radio_channel: "", on_view: "On-View", cad_notes: "" });
  // MDT Self-Init hand-off: prefill the officer block from the signed-on user.
  const fromMdt = new URLSearchParams(window.location.search).get("from") === "mdt";
  // E911 unmatched-queue hand-off: prefill address/coords/phone from a vendor location fix.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const lat = parseFloat(q.get("e911_lat")), lng = parseFloat(q.get("e911_lng"));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setGeo({ lat, lng, verified: true });
    if (q.get("e911_phone")) setCallerPhone(q.get("e911_phone"));
    client.get("/geo/reverse", { params: { lat, lng } }).then((r) => setAddr(r.data.address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`)).catch(() => setAddr(`${lat.toFixed(5)}, ${lng.toFixed(5)}`));
  }, []);
  useEffect(() => {
    if (origin === "officer" && user?.badge && !ofc.reporting_unit) {
      setOfc((o) => ({ ...o, reporting_unit: user.badge, officer_name: user.name || "" }));
    }
  }, [origin, user?.badge]); // eslint-disable-line react-hooks/exhaustive-deps
  const [description, setDescription] = useState("");
  const [premiseNote, setPremiseNote] = useState("");
  const [aiNotes, setAiNotes] = useState(""); const [aiBusy, setAiBusy] = useState(false);
  const [bolo, setBolo] = useState(false); const [sensitive, setSensitive] = useState(false);
  const [multiAgency, setMultiAgency] = useState(false);
  const [units, setUnits] = useState([]); const [assign, setAssign] = useState([]);
  const [unitQ, setUnitQ] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [nearest, setNearest] = useState([]);
  useEffect(() => {
    if (addr.trim().length < 8) { setNearest([]); return; }
    const h = setTimeout(() => client.get("/units/nearest", { params: { address: addr.trim(), lat: geo?.lat, lng: geo?.lng, discipline: multiAgency ? "all" : disc, n: 3 } })
      .then((r) => setNearest(r.data.units || [])).catch(() => setNearest([])), 700);
    return () => clearTimeout(h);
  }, [addr, disc, multiAgency]);
  const [hazard, setHazard] = useState(null);
  const [banAlert, setBanAlert] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cc, setCc] = useState("");
  const [telOpen, setTelOpen] = useState(false);
  const [debrief, setDebrief] = useState(null);
  // EXPANSION 3: configurable priorities, escalation keywords, duplicate detection
  const [prioCfg, setPrioCfg] = useState(null);
  const [escKw, setEscKw] = useState([]);
  const [dups, setDups] = useState([]);
  const attachTo = async (o) => {
    setBusy(true);
    try {
      const r = await client.post(`/incidents/${o.id}/attach-caller`, { phone: callerPhone, name: callerName, ali, note: description });
      toast.success(`Caller attached to ${r.data.cad_number || r.data.cc_number} — no duplicate created`);
      nav(`/incident/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not attach caller"); }
    setBusy(false);
  };
  const skipClearRef = useRef(false);
  useEffect(() => {
    client.get("/priority-config").then((r) => setPrioCfg(r.data.levels)).catch(() => {});
    client.get("/escalation-keywords").then((r) => setEscKw(r.data.keywords)).catch(() => {});
  }, []);
  const nav = useNavigate();
  const routeLoc = useLocation();
  const scenario = routeLoc.state?.scenario;

  // Training practice scenario: pre-fill intake fields (trainee still classifies the call).
  useEffect(() => {
    if (!scenario) return;
    if (scenario.disc) setDisc(scenario.disc);
    if (scenario.address) setAddr(scenario.address);
    if (scenario.description) setDescription(scenario.description);
    if (scenario.priority) setPriority(scenario.priority);
    if (scenario.callerName) setCallerName(scenario.callerName);
    if (scenario.callerPhone) setCallerPhone(scenario.callerPhone);
  }, []); // eslint-disable-line

  // Reserve a CC# the instant the call-for-service intake opens (CAD standard).
  const reservedRef = useRef(false);
  useEffect(() => {
    if (reservedRef.current) return;
    reservedRef.current = true;
    client.post("/incidents/reserve-cc")
      .then((r) => setCc(r.data.cc_number))
      .catch(() => { reservedRef.current = false; });
  }, []);

  const loadTypes = async () => {
    if (q.trim().length >= 2) {
      // Smart search: official name, CAD code, alias, plain language, misspelling.
      const { data } = await client.get("/call-types-smart-search", { params: { q: q.trim(), discipline: disc, limit: 25 } });
      setTypes(data.results);
    } else {
      const { data } = await client.get("/call-types", { params: { discipline: disc, limit: 40 } });
      setTypes(data);
    }
  };
  useEffect(() => {
    if (skipClearRef.current) { skipClearRef.current = false; loadTypes(); return; }
    loadTypes(); setDetail(null);
  }, [disc]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(loadTypes, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line
  useEffect(() => {
    const params = multiAgency ? {} : { discipline: disc };
    client.get("/units", { params }).then((r) => setUnits(r.data.filter((u) => u.status === "available")));
  }, [disc, multiAgency]);

  const pick = async (code) => {
    const { data } = await client.get(`/call-types/${code}`);
    setDetail(data); setPriority(data.priority); setAnswers({}); setGiven({}); setQ("");
    if (data.discipline && data.discipline !== disc) { skipClearRef.current = true; setDisc(data.discipline); }
    if (data.sensitive) setSensitive(true);
  };

  // Escalation scan: description + interview answers + officer narrative.
  const escMatched = useMemo(() => {
    const text = `${description} ${Object.values(answers).join(" ")} ${ofc.cad_notes}`.toLowerCase();
    if (text.trim().length < 3 || !escKw.length) return [];
    return escKw.filter((k) => text.includes(k.term));
  }, [description, answers, ofc.cad_notes, escKw]);
  const escRecommended = escMatched.length ? Math.min(...escMatched.map((k) => k.priority)) : 5;
  const applyEscalation = (rec, reasons) => {
    setDescription((p) => `${p ? p + "\n" : ""}[PRIORITY ESCALATION P${priority} → P${rec} approved by dispatcher — ${reasons}]`);
    setPriority(rec);
    toast.warning(`Priority escalated to P${rec} — change recorded in CAD narrative`);
  };

  useEffect(() => {
    if (!addr.trim() || addr.trim().length < 4) { setHazard(null); return; }
    const tmr = setTimeout(async () => {
      try { const { data } = await client.get("/premise-match", { params: { address: addr, apt: apt || undefined, discipline: disc } });
        setHazard(data.matched && data.hazards.length ? data : null); } catch { setHazard(null); }
      try { const { data: ba } = await client.get("/ban-notices/alert", { params: { address: addr } });
        setBanAlert(ba.matched ? ba.alerts : null); } catch { setBanAlert(null); }
      try { const { data: dd } = await client.get("/incident-duplicates", { params: { address: addr, code: detail?.code } });
        setDups(dd.matches || []); } catch { setDups([]); }
    }, 500);
    return () => clearTimeout(tmr);
  }, [addr, apt, disc]);

  const toggleAssign = (uid) => setAssign((a) => a.includes(uid) ? a.filter((x) => x !== uid) : [...a, uid]);
  const askedCount = useMemo(() => Object.values(answers).filter(Boolean).length, [answers]);
  const givenCount = useMemo(() => Object.values(given).filter(Boolean).length, [given]);

  const buildNarrative = () => {
    const answered = (detail?.questions || []).map((qq, i) => answers[i] ? `Q: ${qq}\nA: ${answers[i]}` : null).filter(Boolean).join("\n");
    return [description.trim(), origin === "officer" ? `OFFICER NARRATIVE: ${ofc.cad_notes}` : "", answered].filter(Boolean).join("\n\n");
  };

  const genNena = async () => {
    if (!detail) { toast.error("Select a call type"); return; }
    const raw = (buildNarrative() || description || "").trim();
    if (raw.length < 10) {
      toast.error("Enter dispatch notes first — type a Description, add an Officer Narrative, or answer the interrogation questions, then generate.");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await client.post("/ai/narrative", { raw_notes: raw, call_type: detail.label });
      setAiNotes(data.narrative);
      toast.success("NENA-formatted notes generated (AI — review before use)");
    } catch (e) { toast.error(e.response?.data?.detail || "AI error"); }
    finally { setAiBusy(false); }
  };

  const create = async () => {
    if (!detail) { toast.error("Select a call type"); return; }
    if (!addr.trim()) { toast.error("Location is required"); return; }
    setBusy(true);
    try {
      const { data } = await client.post("/incidents", {
        discipline: disc, call_type_code: detail.code, priority, origin, cc_number: cc || undefined,
        location: { address: addr, apt: apt || null, beat, district, jurisdiction: jur, lat: geo?.lat ?? null, lng: geo?.lng ?? null, common_name: commonName || null, agency: ctx?.jurisdiction || null },
        caller: origin === "citizen" ? { name: callerName || null, phone: callerPhone || null, ali: ali || null } : {},
        officer: origin === "officer" ? ofc : null,
        narrative: [buildNarrative(), aiNotes && `\n--- AI NENA NOTES (reviewed) ---\n${aiNotes}`].filter(Boolean).join("\n"),
        description, premise_hazards_note: premiseNote, bolo, sensitive, multi_agency: multiAgency,
        assign_units: assign, source: origin === "officer" ? "mdt" : "phone", locate_token: locateToken || undefined, late_entry: lateEntryPayload(),
      });
      toast.success(`Case ${data.cc_number} created${data.public_ref ? ` — CAD# ${data.public_ref}` : ""}`);
      if (scenario?.key) {
        try {
          const { data: db } = await client.post("/training/score", {
            scenario_key: scenario.key, disc, priority, call_type_label: detail?.label,
            prearrival_count: givenCount, cc_number: data.cc_number, session_id: scenario.sessionId,
          });
          setDebrief(db);
          setBusy(false);
          return;
        } catch { /* fall through to normal navigation */ }
      }
      nav(fromMdt ? "/mdt" : `/incident/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const inp = "w-full bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-sm outline-none focus:border-[#3B82F6] font-mono";
  const lbl = "font-mono text-[10px] text-[#9CA3AF] uppercase";
  const isEms = detail?.label?.includes("Cardiac Arrest");

  return (
    <Layout>
      <PageHeader title="New Call Intake" sub="9-1-1 / Officer-originated call entry · protocol-driven interrogation & pre-arrival"
        right={<div data-testid="header-cc" className="font-mono text-[11px] text-[#3B82F6] flex items-center gap-1.5"><Hash size={13} /> {cc || "reserving…"}</div>} />

      <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_380px]">
        {/* FORM */}
        <div className="overflow-y-auto p-4 space-y-4">
          {scenario && (
            <div data-testid="scenario-objective" className="border-l-2 border-[#F59E0B] bg-[#F59E0B]/[0.07] px-4 py-2.5">
              <div className="font-mono text-[10px] text-[#F59E0B] uppercase tracking-wider mb-1">Training Scenario · {scenario.title}</div>
              <div className="text-sm text-[#F3F4F6]">{scenario.objective}</div>
            </div>
          )}
          {/* reserved CC# — assigned the moment intake opens */}
          <div data-testid="cfs-cc-banner" className="flex items-center gap-3 border-l-2 border-[#3B82F6] bg-[#3B82F6]/[0.07] px-4 py-2.5">
            <Hash size={22} className="text-[#3B82F6] shrink-0" />
            <div className="min-w-0 leading-none">
              <div className="font-mono text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Call for Service · CC Number</div>
              <div data-testid="cfs-cc-number" className="font-head font-bold text-2xl tracking-tight text-white leading-none">{cc || "reserving…"}</div>
            </div>
            <span className="ml-auto font-mono text-[10px] font-bold text-[#3B82F6] whitespace-nowrap">
              RESERVED · {origin === "officer" ? "OFFICER ON-VIEW" : "9-1-1 INTAKE"}
            </span>
          </div>

          {/* origin toggle */}
          <div className="flex gap-1">
            <button data-testid="origin-citizen" onClick={() => setOrigin("citizen")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-head border transition-colors ${origin === "citizen" ? "bg-[#3B82F6] border-[#3B82F6] text-white" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF]"}`}>
              <PhoneCall size={14} /> Citizen / 9-1-1 Call</button>
            <button data-testid="origin-officer" onClick={() => setOrigin("officer")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-head border transition-colors ${origin === "officer" ? "bg-[#8B5CF6] border-[#8B5CF6] text-white" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF]"}`}>
              <Radio size={14} /> Officer / MDT Originated</button>
          </div>

          {/* classification */}
          <Section title="Call Classification">
            <div className="flex gap-1 mb-2">
              {["police", "fire", "ems"].map((d) => (
                <button key={d} data-testid={`disc-tab-${d}`} onClick={() => setDisc(d)}
                  className={`px-3 py-1.5 text-xs font-head border ${disc === d ? "border-transparent text-white" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF]"}`}
                  style={disc === d ? { background: d === "police" ? "#3B82F6" : d === "fire" ? "#EF4444" : "#10B981" } : {}}>{d.toUpperCase()}</button>
              ))}
            </div>
            <div className="relative">
              <div className="flex items-center gap-2 bg-[#141518] border border-[#2C2D35] px-2">
                <Search size={13} className="text-[#9CA3AF]" />
                <input data-testid="calltype-search" value={detail ? detail.label : q} onChange={(e) => { setDetail(null); setQ(e.target.value); }}
                  placeholder="Search & select call type…" className="flex-1 bg-transparent py-1.5 text-sm outline-none font-mono" />
                {detail && <span className="font-mono text-[10px] text-[#3B82F6]">{detail.code}</span>}
              </div>
              {!detail && q && (
                <div className="absolute z-20 left-0 right-0 max-h-56 overflow-y-auto border border-[#2C2D35] bg-[#0d0e11]">
                  {types.map((t) => (
                    <button key={t.code} data-testid={`calltype-${t.code}`} onClick={() => pick(t.code)}
                      className="w-full text-left px-3 py-1.5 border-b border-[#2C2D35] hover:bg-[#141518] text-sm font-head flex items-center gap-2">
                      <span className="font-mono text-[10px] text-[#9CA3AF]">{t.code}</span>
                      <span className="truncate">{t.label}</span>
                      {t.suggested && (
                        <span data-testid={`calltype-suggested-${t.code}`} className="ml-auto shrink-0 px-1.5 py-0.5 text-[8px] font-mono font-bold border border-[#F59E0B]/60 text-[#F59E0B] rounded-sm uppercase">
                          Suggested · {t.match}
                        </span>
                      )}
                      {t.discipline !== disc && <span className="shrink-0 font-mono text-[9px] text-[#A855F7] uppercase">{t.discipline}</span>}
                    </button>
                  ))}
                  {types.length === 0 && q.trim().length >= 2 && (
                    <div data-testid="calltype-no-match" className="px-3 py-2 font-mono text-[10px] text-[#F59E0B]">
                      No matching call type — logged for admin review. Use a fallback type below to enter the call NOW.
                    </div>
                  )}
                </div>
              )}
            </div>
            <FallbackRow currentCode={detail?.code} onPick={pick} />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><label className={lbl}>Priority</label>
                <select data-testid="priority-select" value={priority} onChange={(e) => setPriority(Number(e.target.value))} className={inp}>
                  {(prioCfg ? prioCfg.map((l) => [l.level, l.label]) : PRIORITIES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select></div>
              <div><label className={lbl}>Nature Code (auto)</label>
                <input data-testid="nature-code" readOnly value={detail?.code || ""} className={`${inp} text-[#9CA3AF]`} /></div>
            </div>
            <EscalationBanner matched={escMatched} recommended={escRecommended} currentPriority={priority} onApply={applyEscalation} />
          </Section>

          {/* location */}
          <div className="mb-3"><LateEntryPanel value={lateEntry} onChange={setLateEntry} isPolice={disc === "police"} /></div>
          <Section title="Location">
            <label className={lbl}>Address *</label>
            <AddressInput value={addr} onChange={setAddr} onVerified={(g) => { setGeo(g); if (g?.common_name) setCommonName(g.common_name); }} verified={geo} className={inp} />
            {origin === "citizen" && (
              <div className="mt-2">
                <IntakeLocate phone={callerPhone} onToken={setLocateToken} prominent={!addr.trim()}
                  onFix={(fix, address) => { setGeo({ lat: fix.lat, lng: fix.lng, verified: true, source: "caller_gps" }); setAddr(address || `${fix.lat.toFixed(5)}, ${fix.lng.toFixed(5)}`); }} />
              </div>
            )}
            <DuplicateWarning matches={dups} />
            <NearbyOpenCalls geo={geo} onAttach={attachTo} attaching={busy} />
            {hazard && <div className="mt-2"><HazardBanner summary={hazard.summary} hazards={hazard.hazards} fireEmsStaging={hazard.premise?.fire_ems_staging} /></div>}
            {banAlert && banAlert.map((a, i) => (
              <div key={i} data-testid="ban-cad-alert" className="mt-2 border-2 border-[#EF4444] bg-[#1c0d0d] p-3 rounded-sm">
                <div className="font-head font-bold text-sm text-[#EF4444]">⚠ {a.banner}</div>
                <div className="font-mono text-[11px] text-white mt-1">
                  Subject: <b>{a.subject}</b> · Property: {a.property}<br />
                  Notice No.: {a.notice_number} · Served: {a.served ? `${a.served.slice(0, 16)} (${a.served_method})` : "—"} · Effective through: {a.effective_through}<br />
                  Issued at owner/agent direction: {a.issued_at_direction_of}
                </div>
                <div className="font-mono text-[10px] text-[#F59E0B] mt-1">{a.instruction}</div>
              </div>
            ))}
            <div className="grid grid-cols-5 gap-2 mt-2">
              <input data-testid="call-common-name" value={commonName} onChange={(e) => setCommonName(e.target.value)} placeholder="Business / common name" className={`${inp} ${commonName ? "border-[#F59E0B]/60 text-[#FCD34D]" : ""}`} title={ctx?.common_source ? `Source: ${ctx.common_source}` : ""} />
              <input data-testid="call-apt" value={apt} onChange={(e) => setApt(e.target.value)} placeholder="Apt/Unit/Floor" className={inp} />
              <input data-testid="call-beat" value={beat} onChange={(e) => setBeat(e.target.value)} placeholder="Beat / Zone" className={inp} />
              <input data-testid="call-district" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="District" className={inp} />
              <input data-testid="call-jurisdiction" value={jur} onChange={(e) => setJur(e.target.value)} placeholder="Jurisdiction / PD" className={inp} />
            </div>
            {ctx?.ok && (
              <div data-testid="address-context" className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[10px] text-[#9CA3AF]">
                <span className="text-[#6B7280] uppercase">Auto-filled from pin:</span>
                {ctx.police_district && <span className="px-1.5 py-0.5 border border-[#3B82F6]/60 bg-[#3B82F6]/10 text-[#93C5FD] font-bold" data-testid="bpd-district-chip">BPD {ctx.police_district.district.toUpperCase()} DISTRICT · #{ctx.police_district.number}</span>}
                {ctx.pa_jurisdiction && <span className="px-1.5 py-0.5 border border-[#10B981]/60 bg-[#10B981]/10 text-[#6EE7B7] font-bold" data-testid="pa-jurisdiction-chip" title={`${ctx.pa_jurisdiction.service}${ctx.pa_jurisdiction.full_time ? ` · ${ctx.pa_jurisdiction.full_time} FT / ${ctx.pa_jurisdiction.part_time} PT officers` : ""} · PA PCCD`}>PA · {ctx.pa_jurisdiction.municipality?.toUpperCase()} · {ctx.pa_jurisdiction.kind === "psp" ? "PSP COVERAGE" : ctx.pa_jurisdiction.kind.toUpperCase()}</span>}
                {(ctx.pa_jurisdiction || ctx.police_district) && geo?.lat && (
                  <button type="button" data-testid="show-on-map-btn" onClick={() => { sessionStorage.setItem("map_focus_shape", JSON.stringify({ lat: geo.lat, lng: geo.lng, address: addr })); nav("/dispatch"); }}
                    className="px-1.5 py-0.5 border border-[#22D3EE]/60 text-[#67E8F9] hover:bg-[#22D3EE]/20 font-head font-bold flex items-center gap-1"><Map size={10} /> SHOW ON MAP</button>
                )}
                {ctx.psp_station && ctx.pa_jurisdiction?.kind === "psp" && (
                  <span data-testid="psp-station-chip" className="flex items-center gap-1.5 px-1.5 py-0.5 border border-[#6B7280] bg-[#1F2937] text-[#E5E7EB]">
                    <span className="font-bold">PSP {ctx.psp_station.station}</span>
                    <span className="text-[#9CA3AF]">{ctx.psp_station.distance_mi} mi</span>
                    {ctx.psp_station.phone && <a href={`tel:${ctx.psp_station.phone.replace(/[^\d+]/g, "")}`} className="text-[#93C5FD] underline">{ctx.psp_station.phone}</a>}
                    {ctx.psp_station.phone && <button type="button" data-testid="psp-transfer-btn" disabled={!!routed} onClick={() => routeAgency("transfer", { agency: `Pennsylvania State Police — ${ctx.psp_station.station}`, psap: { name: ctx.psp_station.station, phone: ctx.psp_station.phone } })}
                      className="px-1.5 py-0.5 font-head font-bold bg-[#6B7280] text-black hover:bg-[#9CA3AF] disabled:opacity-50">↗ TRANSFER TO PSP</button>}
                  </span>
                )}
                {ctx.jurisdiction && <span>Responsible PD <span className="text-white">{ctx.jurisdiction}</span></span>}
                {ctx.psap?.name && <span>PSAP <span className="text-white">{ctx.psap.name}</span>{ctx.psap.phone ? <span className="text-[#93C5FD]"> {ctx.psap.phone}</span> : null}</span>}
                {ctx.county && <span>{ctx.county} County</span>}
                {ctx.state && <span>{ctx.state}{ctx.postcode ? ` ${ctx.postcode}` : ""}</span>}
                {ctx.common_name && <span>Common place <span className="text-[#FCD34D]">{ctx.common_name}</span> <span className="text-[#6B7280]">({ctx.common_source})</span></span>}
                <span className="text-[#6B7280]">· editable</span>
                {commonName && geo?.lat && (
                  <button type="button" data-testid="save-alias-btn" onClick={async () => {
                    const alias = window.prompt(`Short alias for "${commonName}" (e.g. JHH)?`, "");
                    if (alias === null) return;
                    try { await client.post("/ali/places", { name: commonName, alias: alias.trim() || null, address: addr, lat: geo.lat, lng: geo.lng }); toast.success(`Saved "${commonName}"${alias.trim() ? ` as "${alias.trim()}"` : ""} — type it next time to jump here`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Could not save alias"); }
                  }} className="px-1.5 py-0.5 border border-[#F59E0B]/60 text-[#FCD34D] hover:bg-[#F59E0B]/20 font-head font-bold">+ SAVE ALIAS</button>
                )}
              </div>
            )}
            {ctx?.ok && ctx.jurisdiction && !ctx.in_system && (
              <div data-testid="agency-route" className="mt-2 border border-[#A78BFA] bg-[#A78BFA]/10 px-3 py-2">
                <div className="font-mono text-[10px] font-bold uppercase text-[#C4B5FD] flex items-center gap-1.5"><Radio size={12} /> Outside agency — this address belongs to <span className="text-white">{ctx.jurisdiction}</span></div>
                <div className="font-mono text-[10px] text-[#D1D5DB] mt-0.5">{ctx.psap?.name ? <>PSAP: {ctx.psap.name} {ctx.psap.phone ? <a className="text-[#93C5FD] underline" href={`tel:${ctx.psap.phone.replace(/[^\d+]/g, "")}`}>{ctx.psap.phone}</a> : <span className="text-[#9CA3AF]">— no number on file</span>}</> : "No PSAP record on file for this county"}{routed && <span className="ml-2 text-[#22C55E] font-bold">· {routed === "transfer" ? "TRANSFERRED" : "MUTUAL-AID SENT"}</span>}</div>
                <div className="flex gap-2 mt-1.5">
                  <button type="button" data-testid="agency-transfer-btn" disabled={!!routed} onClick={() => routeAgency("transfer")} className="px-2.5 py-1 font-head font-bold text-[10px] bg-[#A78BFA] text-black hover:bg-[#c4b5fd] disabled:opacity-50">↗ TRANSFER CALL{ctx.psap?.phone ? " & DIAL PSAP" : ""}</button>
                  <button type="button" data-testid="agency-mutual-aid-btn" disabled={!!routed} onClick={() => routeAgency("mutual_aid")} className="px-2.5 py-1 font-head font-bold text-[10px] border border-[#A78BFA] text-[#C4B5FD] hover:bg-[#A78BFA]/20 disabled:opacity-50">MUTUAL-AID NOTICE</button>
                </div>
              </div>
            )}
          </Section>

          {/* caller vs officer */}
          {origin === "citizen" ? (
            <Section title="Caller Information" icon={User}>
              <div className="grid grid-cols-3 gap-2">
                <div><label className={lbl}>Caller Name</label>
                  <input data-testid="call-caller-name" value={callerName} onChange={(e) => setCallerName(e.target.value)} placeholder="Caller name" className={inp} /></div>
                <div><label className={lbl}>Phone — ANI</label>
                  <AniInput value={callerPhone} onChange={setCallerPhone} className={inp}
                    onPick={(s) => { if (!callerName.trim()) setCallerName(s.name); setAli(`${s.kind.toUpperCase()} · ${s.source}${s.address ? ` · ${s.address}` : ""}`); if (!addr.trim() && s.address && s.kind !== "prior caller") { setAddr(s.address); if (s.lat && s.lng) setGeo({ lat: s.lat, lng: s.lng, verified: true, source: "ali" }); } toast.success(`ALI: ${s.name}`); }}
                    onUseAddress={(a) => { setAddr(a.address); if (a.lat && a.lng) setGeo({ lat: a.lat, lng: a.lng, verified: true, source: "ali" }); toast.success("Last known address applied — confirm with caller"); }}
                    attaching={busy}
                    onUseName={(n) => setCallerName(n)}
                    onAttach={attachTo} /></div>
                <div><label className={lbl}>Location — ALI</label>
                  <input data-testid="call-caller-ali" value={ali} onChange={(e) => setAli(e.target.value)} placeholder="Automatic Location ID" className={inp} /></div>
              </div>
              <button data-testid="tel-intel-btn" type="button" onClick={() => setTelOpen(true)}
                className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-head border border-[#3B82F6]/50 text-[#3B82F6] hover:bg-[#3B82F6]/10 transition-colors">
                <Phone size={13} /> Telephone Intelligence &amp; Call History
              </button>
            </Section>
          ) : (
            <Section title="Officer / MDT Call for Service" icon={Radio} accent="#8B5CF6">
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="ofc-unit" value={ofc.reporting_unit} onChange={(e) => setOfc({ ...ofc, reporting_unit: e.target.value })} placeholder="Reporting Unit" className={inp} />
                <input data-testid="ofc-name" value={ofc.officer_name} onChange={(e) => setOfc({ ...ofc, officer_name: e.target.value })} placeholder="Officer Name / Badge" className={inp} />
                <input data-testid="ofc-channel" value={ofc.radio_channel} onChange={(e) => setOfc({ ...ofc, radio_channel: e.target.value })} placeholder="Radio Channel" className={inp} />
                <select data-testid="ofc-onview" value={ofc.on_view} onChange={(e) => setOfc({ ...ofc, on_view: e.target.value })} className={inp}>
                  <option>On-View</option><option>Self-Initiated</option><option>Flag-Down</option></select>
              </div>
              <textarea data-testid="ofc-notes" value={ofc.cad_notes} onChange={(e) => setOfc({ ...ofc, cad_notes: e.target.value })} rows={2} placeholder="Officer narrative / CAD notes" className={`${inp} mt-2`} />
            </Section>
          )}

          {/* unit assignment — searchable + scrollable, selected units pinned on top */}
          <Section title={`Unit Assignment (${assign.length} selected)`} icon={Siren}>
            {units.length === 0 && <div className="text-xs font-mono text-[#9CA3AF]">No available {multiAgency ? "" : disc} units.</div>}
            {multiAgency && units.length > 0 && <div className="text-[10px] font-mono text-[#A855F7] mb-1.5">Multi-agency: showing all available Police, Fire & EMS units.</div>}
            {units.length > 0 && (
              <div className="flex items-center gap-2 bg-[#141518] border border-[#2C2D35] px-2 py-1.5 mb-2 focus-within:border-[#3B82F6]">
                <Search size={13} className="text-[#6B7280]" />
                <input data-testid="assign-unit-search" value={unitQ} onChange={(e) => setUnitQ(e.target.value)}
                  placeholder={`Search ${units.length} available units — unit # / name / county…`}
                  className="flex-1 bg-transparent outline-none text-xs font-mono text-white placeholder:text-[#6B7280]" />
                {unitQ && <button onClick={() => setUnitQ("")} className="text-[#6B7280] hover:text-white text-xs">✕</button>}
              </div>
            )}
            {assign.length > 0 && (
              <div data-testid="assign-selected-row" className="flex flex-wrap gap-1.5 mb-2 pb-2 border-b border-[#2C2D35]">
                {assign.map((uid) => (
                  <button key={uid} data-testid={`assign-selected-${uid}`} onClick={() => toggleAssign(uid)}
                    title="Tap to unselect"
                    className="flex items-center gap-1 px-2 py-1 text-xs font-mono border border-[#3B82F6] bg-[#3B82F6]/20 text-white rounded-sm">
                    <CheckSquare size={12} /> <b>{uid}</b> ✕
                  </button>
                ))}
              </div>
            )}
            <div className="max-h-72 overflow-y-auto pr-1 space-y-2" data-testid="assign-unit-list">
              {nearest.length > 0 && !unitQ.trim() && (
                <div data-testid="assign-nearest" className="border border-[#10B981]/50 bg-[#10B981]/5 p-1.5">
                  <div className="flex items-center gap-2 px-1 pb-1 text-[10px] font-head uppercase tracking-wider text-[#10B981]"><Zap size={11} /> Closest available to this address</div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {nearest.map((u) => (
                      <button key={u.unit_id} data-testid={`assign-nearest-${u.unit_id}`} onClick={() => toggleAssign(u.unit_id)}
                        className={`flex items-center gap-1.5 px-2 py-1.5 text-xs font-mono border ${assign.includes(u.unit_id) ? "border-[#3B82F6] bg-[#3B82F6]/15 text-white" : "border-[#10B981]/40 bg-[#141518] text-[#C9CDD4]"}`}>
                        {assign.includes(u.unit_id) ? <CheckSquare size={13} /> : <Square size={13} />}<b>{u.unit_id}</b><span className="truncate">{u.personnel}</span><span className="ml-auto text-[#10B981] text-[10px]">{u.blocks} blk</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {(() => {
                const uq2 = unitQ.trim().toLowerCase();
                const shown = uq2 ? units.filter((u) => `${u.unit_id} ${u.personnel || ""} ${u.county || ""} ${u.agency || ""}`.toLowerCase().includes(uq2)) : units;
                if (units.length > 0 && shown.length === 0) return <div className="text-[11px] font-mono text-[#9CA3AF] py-2">No units match “{unitQ}”.</div>;
                const groupOf = (u) => u.county ? `${u.county} County` : (u.agency || (u.type ? `${u.type.toUpperCase()} units` : "Other units"));
                const groups = {};
                shown.forEach((u) => { (groups[groupOf(u)] ||= []).push(u); });
                const names = Object.keys(groups).sort((a, b) => (groups[b].length - groups[a].length) || a.localeCompare(b));
                const UnitBtn = ({ u }) => (
                  <button key={u.unit_id} data-testid={`assign-unit-${u.unit_id}`} onClick={() => toggleAssign(u.unit_id)}
                    className={`flex items-center gap-2 px-2 py-1.5 text-xs font-mono border transition-colors ${assign.includes(u.unit_id) ? "border-[#3B82F6] bg-[#3B82F6]/15 text-white" : "border-[#2C2D35] bg-[#141518] text-[#9CA3AF] hover:border-[#4B5563]"}`}>
                    {assign.includes(u.unit_id) ? <CheckSquare size={13} /> : <Square size={13} />}
                    {multiAgency && <span className="font-bold" style={{ color: DISC[u.type]?.color || "#9CA3AF" }}>{(u.type || "").toUpperCase()}</span>}
                    <b>{u.unit_id}</b> <span className="truncate">{u.personnel}</span>
                  </button>
                );
                return names.map((g) => {
                  const collapsed = collapsedGroups[g] && !uq2;
                  return (
                    <div key={g} data-testid={`assign-group-${g.replace(/\s+/g, "-").toLowerCase()}`}>
                      <button type="button" onClick={() => setCollapsedGroups((p) => ({ ...p, [g]: !p[g] }))}
                        className="w-full flex items-center gap-2 px-1 py-1 text-[10px] font-head uppercase tracking-wider text-[#9CA3AF] hover:text-white">
                        <span className="text-[#6B7280]">{collapsed ? "▸" : "▾"}</span> {g}
                        <span className="font-mono text-[9px] text-[#6B7280]">{groups[g].length} available{groups[g].filter((u) => assign.includes(u.unit_id)).length ? ` · ${groups[g].filter((u) => assign.includes(u.unit_id)).length} selected` : ""}</span>
                        <span className="flex-1 border-t border-[#2C2D35] ml-1" />
                      </button>
                      {!collapsed && <div className="grid grid-cols-2 gap-1.5 mt-1">{groups[g].map((u) => <UnitBtn key={u.unit_id} u={u} />)}</div>}
                    </div>
                  );
                });
              })()}
            </div>
          </Section>

          {/* incident details + dispatch notes — ONE merged CAD record section */}
          <Section title="Incident Details & Dispatch Notes (CAD Record)" icon={Sparkles} accent="#F59E0B">
            <label className={lbl}>Description — what is happening</label>
            <div className="flex flex-wrap gap-1 mb-1" data-testid="dispatch-quick-notes">
              {["Caller states", "Third-party caller", "Subject last seen wearing", "Weapons mentioned", "Caller wishes to remain anonymous", "In progress now", "Occurred earlier", "Caller will meet units", "No suspect description", "Repeat call at this location"].map((qn) => (
                <button key={qn} type="button" onClick={() => setDescription((p) => (p ? p + " " : "") + qn + " ")} className="px-2 py-0.5 text-[9px] font-mono border border-[#2C2D35] text-[#22D3EE] rounded-sm hover:border-[#22D3EE]">{qn}</button>
              ))}
            </div>
            <textarea data-testid="call-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={inp} placeholder="What is happening…" />
            <label className={`${lbl} mt-2 block`}>Premise Hazards (notes)</label>
            <input data-testid="call-premise-note" value={premiseNote} onChange={(e) => setPremiseNote(e.target.value)} className={inp} placeholder="Observed hazards…" />

            {/* LIVE CAD NOTES — description + officer narrative + answered protocol Q&A */}
            <div className="flex items-center gap-2 mt-3 mb-1">
              <span className={lbl} style={{ marginBottom: 0 }}>Dispatch Notes — live CAD record</span>
              <span className="font-mono text-[9px] text-[#10B981]">ANSWERED INTERVIEW QUESTIONS APPEND HERE AUTOMATICALLY</span>
            </div>
            <div data-testid="live-dispatch-notes" className="border border-[#F59E0B]/40 bg-[#141518] px-2.5 py-2 font-mono text-[11px] text-[#E5E7EB] whitespace-pre-wrap min-h-[56px] max-h-44 overflow-y-auto rounded-sm">
              {buildNarrative() || <span className="text-[#6B7280]">Type a description or answer the caller-interview questions (right) — everything composes here and is written to the CAD record when the call is created.</span>}
            </div>

            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button data-testid="gen-nena-btn" onClick={genNena} disabled={aiBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-head bg-[#F59E0B] text-black hover:bg-[#d97706] disabled:opacity-50">
                {aiBusy && <Loader2 size={13} className="animate-spin" />} Generate NENA Notes (AI)</button>
              <span data-testid="nena-hint" className="text-[10px] font-mono text-[#9CA3AF]">AI rewrites the live notes above into a NENA-style CAD narrative</span>
            </div>
            <textarea data-testid="ai-notes" value={aiNotes} onChange={(e) => setAiNotes(e.target.value)} rows={3} className={`${inp} mt-2`} placeholder="AI dispatch notes (labeled AI-generated — review before use)…" />
            <div className="flex flex-wrap gap-4 mt-2">
              <label className="flex items-center gap-2 text-xs font-mono text-[#EF4444]"><input type="checkbox" data-testid="bolo-toggle" checked={bolo} onChange={(e) => setBolo(e.target.checked)} /> BOLO Alert</label>
              <label className="flex items-center gap-2 text-xs font-mono text-[#F59E0B]"><input type="checkbox" data-testid="sensitive-toggle" checked={sensitive} onChange={(e) => setSensitive(e.target.checked)} /> Sensitive</label>
              <label className="flex items-center gap-2 text-xs font-mono text-[#A855F7]"><input type="checkbox" data-testid="multi-agency-toggle" checked={multiAgency} onChange={(e) => setMultiAgency(e.target.checked)} /> Multi-Agency (Police + Fire + EMS)</label>
            </div>
            {multiAgency && (
              <div data-testid="multi-agency-note" className="mt-2 px-3 py-2 text-[11px] font-mono text-[#A855F7] border border-[#A855F7]/40 bg-[#A855F7]/10">
                Multi-agency response: Police, Fire & EMS will be requested. Units from any discipline can be attached to this incident.
              </div>
            )}
          </Section>

          <button data-testid="create-incident-btn" onClick={create} disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563eb] text-white font-head font-semibold py-3 text-sm transition-colors disabled:opacity-50">
            {busy && <Loader2 size={15} className="animate-spin" />} Create Incident · Case# {cc || "reserving…"}
          </button>
        </div>

        {/* RIGHT: pre-arrival + interview — 9-1-1 call-taker only; officer on-view calls don't need caller interrogation */}
        <div className="border-l border-[#2C2D35] overflow-y-auto bg-[#0d0e11]">
          {origin === "officer" && <div data-testid="prearrival-hidden-officer" className="p-4 text-xs text-[#9CA3AF] font-mono">Officer-initiated call — pre-arrival instructions &amp; caller interview are not applicable. Enter location, type and notes on the left.</div>}
          {origin !== "officer" && !detail && <div className="p-4 text-xs text-[#9CA3AF] font-mono">Select a call type to load protocol questions & pre-arrival instructions.</div>}
          {origin !== "officer" && detail && (
            <>
              <div className="p-3 border-b border-[#2C2D35]">
                <div className="flex items-center gap-2 mb-1 text-[#10B981]"><HeartPulse size={14} />
                  <span className="font-head font-semibold text-sm">PRE-ARRIVAL INSTRUCTIONS</span>
                  <span data-testid="prearrival-counter" className="ml-auto font-mono text-[11px] text-[#9CA3AF]">{givenCount}/{detail.pre_arrival.length} given</span></div>
                <div className="h-1 rounded bg-[#1c1d22] mb-2"><div className="h-1 rounded transition-all" style={{ width: `${detail.pre_arrival.length ? (givenCount / detail.pre_arrival.length) * 100 : 0}%`, background: "#10B981" }} /></div>
                <div className="font-mono text-[9px] text-[#6B7280] mb-1.5 uppercase">Tap each instruction as you give it · critical items flagged red</div>
                <ol data-testid="prearrival-list" className="space-y-1.5">
                  {detail.pre_arrival.map((p, i) => {
                    const crit = CRIT.test(p); const done = !!given[i];
                    return (
                      <li key={i} data-testid={`prearrival-${i}`} role="button" tabIndex={0}
                        onClick={() => setGiven({ ...given, [i]: !done })}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setGiven({ ...given, [i]: !done }); } }}
                        className="text-xs px-2 py-1.5 leading-snug cursor-pointer border-l-2 flex gap-2 focus:outline-none focus:ring-1 focus:ring-[#3B82F6]"
                        style={{ borderColor: crit ? "#DC2626" : "#10B981", background: done ? "#10B98118" : (crit ? "#dc26260f" : "#141518") }}>
                        {done ? <CheckSquare size={13} className="text-[#10B981] mt-0.5 shrink-0" /> : <Square size={13} className="text-[#9CA3AF] mt-0.5 shrink-0" />}
                        <span className={done ? "line-through text-[#9CA3AF]" : ""}>
                          {crit && <span className="font-mono text-[9px] font-bold text-[#DC2626] mr-1 inline-flex items-center gap-0.5"><AlertTriangle size={9} />CRITICAL</span>}
                          {p}
                        </span>
                      </li>
                    );
                  })}
                </ol>
                {isEms && (
                  <div className="mt-3 border border-[#DC2626]/50 bg-[#1a0b0c] p-2">
                    <div className="font-head text-[11px] font-bold text-[#DC2626] mb-1">CPR QUICK REFERENCE</div>
                    <div className="text-[11px] font-mono text-[#F3F4F6] space-y-0.5">
                      <div>• Center of chest, heel of hand</div>
                      <div>• Push HARD & FAST — 100–120/min</div>
                      <div>• Depth ~2 inches, full recoil</div>
                      <div>• Do NOT stop until help arrives / AED ready</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-3">
                <div className="flex items-center gap-2 mb-1 text-[#3B82F6]"><ClipboardList size={14} />
                  <span className="font-head font-semibold text-sm">CALLER INTERVIEW — {detail.questions.length} QUESTIONS</span>
                  <span data-testid="interview-counter" className="ml-auto font-mono text-[11px] text-[#9CA3AF]">{askedCount}/{detail.questions.length} asked</span></div>
                <div className="h-1 rounded bg-[#1c1d22] mb-2"><div className="h-1 rounded transition-all" style={{ width: `${detail.questions.length ? (askedCount / detail.questions.length) * 100 : 0}%`, background: "#3B82F6" }} /></div>
                <div className="font-mono text-[9px] text-[#6B7280] mb-1.5 uppercase">Answers append to the Dispatch Notes (CAD record) automatically — see live preview at left</div>
                <div className="space-y-1.5">
                  {detail.questions.map((qq, i) => (
                    <div key={i} data-testid={`question-${i}`} className="bg-[#141518] px-2.5 py-2 rounded-sm border transition-colors"
                      style={{ borderColor: "#2C2D35", borderLeft: `2px solid ${answers[i] ? "#3B82F6" : "#2C2D35"}` }}>
                      <div className="flex gap-2 items-start">
                        <button data-testid={`question-check-${i}`} onClick={() => setAnswers({ ...answers, [i]: answers[i] || "✓ asked" })} className="mt-0.5 shrink-0" title="Mark asked">
                          {answers[i] ? <CheckSquare size={14} className="text-[#3B82F6]" /> : <Square size={14} className="text-[#9CA3AF]" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium leading-snug">{i + 1}. {qq}</div>
                          <input data-testid={`answer-${i}`} value={answers[i] === "✓ asked" ? "" : (answers[i] || "")} onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })}
                            placeholder="Caller response — appends to dispatch notes…" className="mt-1 w-full bg-[#0d0e11] border border-[#2C2D35] px-2 py-1 text-xs outline-none focus:border-[#3B82F6] font-mono" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <TelephoneIntel open={telOpen} onClose={() => setTelOpen(false)} defaultNumber={callerPhone} />
      <ScenarioDebrief open={!!debrief} data={debrief} onClose={() => { setDebrief(null); nav("/"); }} />
    </Layout>
  );
}

function Section({ title, children, icon: Icon, accent = "#3B82F6" }) {
  return (
    <div className="border border-[#2C2D35]">
      <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2" style={{ color: accent }}>
        {Icon && <Icon size={13} />} {title}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}
